import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomySize,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";

// Mined from designSystem/Shortcut/Shortcut.module.scss.

const parts: readonly AnatomyPart[] = [
    {
        id: "chip",
        label: "Key chip",
        element:
            "<kbd class=.key>, square var(--shortcut-key-size); text keys hug their label",
    },
    {
        id: "glyph",
        label: "Glyph",
        element:
            "label text or Figma SVG inside .key, painted with currentColor",
    },
    {
        id: "separator",
        label: '"then"',
        element: ".then between the steps of a sequence",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
    {
        id: "disabled",
        label: "In disabled button",
        selector: ":is(button:disabled *)",
    },
];

const keySize = (size: string): readonly AnatomyBinding[] => [
    {
        partId: "chip",
        property: "width",
        literal: size,
        via: "--shortcut-key-size",
        note: "ds-deviation: keycap dimension, no size token family.",
    },
    {
        partId: "chip",
        property: "height",
        literal: size,
        via: "--shortcut-key-size",
    },
];

const sizes: readonly AnatomySize[] = [
    { id: "md", label: "20px", selector: "", bindings: keySize("20px") },
    { id: "sm", label: "16px", selector: ".sm", bindings: keySize("16px") },
];

const glyphSize = (token: `--${string}`): AnatomyBinding => ({
    partId: "glyph",
    property: "font-size",
    token,
});

const sharedBindings: readonly AnatomyBinding[] = [
    {
        partId: "chip",
        property: "gap (chord)",
        token: "--space-2",
        note: "Declared on .chord, between the keys of one chord.",
    },
    { partId: "chip", property: "background", token: "--bg-primary" },
    { partId: "chip", property: "border-width", token: "--border-width" },
    { partId: "chip", property: "border-color", token: "--border-default" },
    { partId: "chip", property: "box-shadow", token: "--shadow-1" },
    { partId: "chip", property: "border-radius", token: "--radius-xs" },
    { partId: "glyph", property: "color", token: "--fg-secondary" },
    { partId: "glyph", property: "font-family", token: "--font-family-base" },
    { partId: "glyph", property: "font-weight", token: "--font-weight-400" },
    { partId: "glyph", property: "line-height", literal: "1" },
    { partId: "glyph", property: "letter-spacing", token: "--tracking-base" },
    {
        partId: "separator",
        property: "gap",
        token: "--space-4",
        note: 'Declared on the .shortcut row, on both sides of "then".',
    },
    { partId: "separator", property: "color", token: "--fg-secondary" },
    { partId: "separator", property: "font-size", token: "--font-size-11" },
    {
        partId: "separator",
        property: "letter-spacing",
        token: "--tracking-base",
    },
];

const disabledBindings: readonly AnatomyBinding[] = [
    { partId: "glyph", property: "color", token: "--fg-disabled" },
    { partId: "chip", property: "box-shadow", literal: "none" },
];

const textMinWidth = (size: string): readonly AnatomyBinding[] => [
    {
        partId: "chip",
        property: "min-width",
        literal: size,
        via: "--shortcut-key-size",
    },
];

// Every key type fades the same way inside a disabled button.
const variant = (
    fields: Omit<AnatomyVariant, "stateBindings">,
): AnatomyVariant => ({
    ...fields,
    stateBindings: { disabled: disabledBindings },
});

const variants: readonly AnatomyVariant[] = [
    variant({
        id: "letter",
        label: "Letter",
        selector: " .key.letter",
        restingBindings: [glyphSize("--font-size-2xs")],
    }),
    variant({
        id: "text",
        label: "Text",
        selector: " .key.text",
        restingBindings: [
            {
                partId: "chip",
                property: "width",
                literal: "auto",
                note: "Text keys (Esc, Ctrl, Alt, Win) hug their label.",
            },
            { partId: "chip", property: "padding-inline", token: "--space-4" },
            glyphSize("--font-size-2xs"),
            {
                partId: "glyph",
                property: "font-weight",
                token: "--font-weight-500",
            },
        ],
        sizeBindings: { md: textMinWidth("20px"), sm: textMinWidth("16px") },
    }),
    variant({
        id: "symbol",
        label: "Symbol",
        selector: " .key.symbol",
        restingBindings: [
            {
                ...glyphSize("--font-size-11"),
                note: "⌘ ⌥ ⌃ ⌫ ⇥ at Figma's 11px, an exception token.",
            },
        ],
    }),
    variant({
        id: "equal",
        label: "Equal",
        selector: " .key.equal",
        restingBindings: [glyphSize("--font-size-xs")],
    }),
    variant({
        id: "icon",
        label: "Icon",
        selector: " .key",
        restingBindings: [
            {
                partId: "glyph",
                property: "size",
                literal: "SVG root",
                note: "Figma-exported SVGs keep their own width and height; arrows rotate per direction.",
            },
        ],
    }),
];

export const shortcutAnatomy: ComponentAnatomySpec = {
    label: "Shortcut",
    schematic: "shortcut",
    rootSelector: ".shortcut",
    variantAxisLabel: "Key type",
    parts,
    states,
    sharedBindings,
    variants,
    sizes,
};
